import { List, Map } from 'immutable';
import {
    convertNumberToE164,
    numberIsValidMatcher,
    removeExtraCharacters,
} from 'flow/utils/call-utils';
import { ContactFieldType } from 'flow/utils/contact-utils';

type ImmutableMap = Map<string, unknown>;

const TOP_LEVEL_NUMBER_PATHS = [
    ['fixed', 'number'],
    ['mobile', 'number'],
    ['phoneNumber', 'number'],
    ['altTelephone1', 'number'],
    ['altTelephone2', 'number'],
];

export const normalizePriorityNumber = (
    number: string | undefined | null,
    countryCode: string,
): string | null => {
    if (typeof number !== 'string') {
        return null;
    }
    const stripped = removeExtraCharacters(number).replace(/\*$/, '');
    if (!stripped) {
        return null;
    }
    return convertNumberToE164(stripped, countryCode);
};

export const getContactNumbers = (
    contact: ImmutableMap,
    countryCode: string,
): string[] => {
    const editableNumbers = (
        contact.getIn(
            ['editableContactDTO', 'contactFields'],
            Map(),
        ) as ImmutableMap
    )
        .valueSeq()
        .filter((field: ImmutableMap) =>
            ContactFieldType.NUMBER.is(field.get('fieldType')),
        )
        .map((field: ImmutableMap) => field.getIn(['number', 'number']));

    const topLevelNumbers = TOP_LEVEL_NUMBER_PATHS.map((path) =>
        contact.getIn(path),
    );

    const normalized = [...editableNumbers.toArray(), ...topLevelNumbers]
        .map((number) => normalizePriorityNumber(number as string, countryCode))
        .filter((number): number is string => numberIsValidMatcher(number));

    return Array.from(new Set(normalized));
};

export const findPriorityEntriesForNumbers = (
    priorityEntries: Map<string, ImmutableMap>,
    numbers: string[],
    countryCode: string,
): List<ImmutableMap> =>
    priorityEntries
        .valueSeq()
        .filter((entry) => {
            const matcher = normalizePriorityNumber(
                entry.get('matcher') as string,
                countryCode,
            );
            return matcher !== null && numbers.includes(matcher);
        })
        .toList();
